/**
 * NaraShape recipe listing page for /admin/document-recipes route.
 * Lists available document recipes and allows creating new ones.
 * Uses KiiList with client-side search and input mode filter.
 */

import { useState, useCallback, useMemo } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { adminRoutes, useWorkspaceUrl } from "../lib/routes";
import { PageHeader } from "../components/layout/PageHeader";
import { RecipeModeBadge } from "../components/RecipeModeBadge";
import { STARTER_PRODUCT_SCHEMA, extractionRefusal } from "../lib/productSchema";
import {
  getShapeRecipes,
  createShapeRecipe,
  type ShapeRecipe,
} from "../services/naraDoor";
import { FileText, Plus, Loader2 } from "lucide-react";
import { useProfile } from "../contexts/ProfileContext";
import { KiiList, KiiDialogLayout, type KiiListColumn, type FilterConfig, type FilterValues } from "../components/kii";
import { TableRow, TableCell } from "../components/ui/table";

const columns: KiiListColumn[] = [
  { key: "name", label: "Name" },
  { key: "input_mode", label: "Input Mode" },
  { key: "status", label: "Status" },
  { key: "description", label: "Description" },
];

const inputModeFilterOptions = [
  { value: "transcription", label: "Transcription" },
  { value: "project", label: "Project" },
  { value: "extraction", label: "Extraction" },
];

const inputModeFilters: FilterConfig[] = [
  {
    id: "input_mode",
    label: "Input Mode",
    placeholder: "All input modes",
    colorVariant: "primary",
    multiple: false,
    options: inputModeFilterOptions,
  },
];

export function NaraShape() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin, isLoading: isProfileLoading } = useProfile();
  const urls = useWorkspaceUrl();

  // Search & filter state
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [filterValues, setFilterValues] = useState<FilterValues>({ input_mode: null });

  // Create template dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [templateLabel, setTemplateLabel] = useState("");
  const [newMode, setNewMode] = useState("transcription");
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Redirect non-admins to dashboard
  if (!isProfileLoading && !isAdmin) {
    return <Navigate to={urls?.dashboard || "/dashboard"} replace />;
  }

  const { data, isLoading } = useQuery({
    queryKey: ["shapeRecipes"],
    queryFn: () => getShapeRecipes(),
  });

  const allTemplates = data?.recipes || [];

  // Client-side search + input_mode filter
  const templates = useMemo(() => {
    let filtered = allTemplates;
    const inputModeFilter = filterValues.input_mode as string | null;
    if (inputModeFilter) {
      filtered = filtered.filter((t) => (t.input_mode || "transcription") === inputModeFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          t.description?.toLowerCase().includes(q)
      );
    }
    return filtered;
  }, [allTemplates, filterValues.input_mode, search]);

  const handleSearch = useCallback((query: string) => {
    setSearch(query.trim());
  }, []);

  const handleSearchQueryChange = useCallback((query: string) => {
    setSearchInput(query);
  }, []);

  const handleFilterChange = useCallback((newFilters: FilterValues) => {
    setFilterValues(newFilters);
  }, []);

  const handleSaveTemplate = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmedLabel = templateLabel.trim();
    setCreateError(null);

    if (!trimmedLabel) {
      setCreateError(t("naraShape.errors.nameRequired"));
      return;
    }

    setIsSavingTemplate(true);
    try {
      await createShapeRecipe({
        name: trimmedLabel,
        input_mode: newMode,
        // nara-media refuses an extraction recipe without a product schema.
        ...(newMode === "extraction" && { schema: STARTER_PRODUCT_SCHEMA }),
      });
      setShowCreateDialog(false);
      setTemplateLabel("");
      setNewMode("transcription");
      setCreateError(null);
      queryClient.invalidateQueries({ queryKey: ["shapeRecipes"] });
    } catch (error: any) {
      console.error("Failed to create recipe", { inputMode: newMode, error: error?.message });
      setCreateError(extractionRefusal(error) ? t("productSchema.errors.refused") : error.message || t("naraShape.errors.createFailed"));
    } finally {
      setIsSavingTemplate(false);
    }
  };

  return (
    <div>
      <PageHeader
        title={t("naraShape.title")}
        description={t("naraShape.description")}
        isAdmin
        breadcrumbs={[
          { label: t("naraShape.breadcrumbs.admin") },
          { label: t("naraShape.breadcrumbs.documentRecipes") },
        ]}
        actions={
          <button
            onClick={() => setShowCreateDialog(true)}
            className="inline-flex items-center gap-2 px-3 py-2 bg-nara-orange text-nara-charcoal font-medium rounded-lg hover:bg-nara-orange/90 transition-colors"
          >
            <Plus className="h-4 w-4" />
            {t("naraShape.createTemplate")}
          </button>
        }
      />

      <KiiList<ShapeRecipe>
        filters={inputModeFilters}
        filterValues={filterValues}
        onFilterChange={handleFilterChange}
        searchQuery={searchInput}
        onSearchQueryChange={handleSearchQueryChange}
        onSearch={handleSearch}
        columns={columns}
        data={templates}
        isLoading={isLoading}
        emptyMessage={search || filterValues.input_mode ? t("naraShape.empty.searchHint") : t("naraShape.empty.title")}
        emptyIcon={<FileText className="h-10 w-10 text-muted-foreground" />}
        showCount={true}
        countLabel={(count) => t("naraShape.templateCount", { count })}
        onRowClick={(template) => navigate(adminRoutes.documentRecipe(template.id))}
        renderRow={(template) => (
          <TableRow key={template.id}>
            <TableCell>
              <div className="flex items-center gap-3">
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="font-medium">
                  {template.name || t("naraShape.unnamedTemplate")}
                </span>
              </div>
            </TableCell>
            <TableCell>
              <RecipeModeBadge mode={template.input_mode || "transcription"} />
            </TableCell>
            <TableCell>
              {template.status === "published" ? (
                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300">
                  Published
                </span>
              ) : template.status === "published_with_draft" ? (
                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300">
                  Has changes
                </span>
              ) : template.status === "draft" ? (
                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                  Draft
                </span>
              ) : (
                <span className="text-muted-foreground">&mdash;</span>
              )}
            </TableCell>
            <TableCell className="text-muted-foreground">
              {template.description || t("naraShape.noDescription")}
            </TableCell>
          </TableRow>
        )}
      />

      {/* Create Template Dialog */}
      <KiiDialogLayout
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        title={t("naraShape.createDialog.title")}
        description={t("naraShape.createDialog.description")}
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => {
                setShowCreateDialog(false);
                setCreateError(null);
              }}
              disabled={isSavingTemplate}
              className="px-4 py-2 text-foreground border border-border rounded-lg hover:bg-accent transition-colors disabled:opacity-50"
            >
              {t("naraShape.createDialog.cancel")}
            </button>
            <button
              onClick={() => handleSaveTemplate()}
              disabled={!templateLabel.trim() || isSavingTemplate}
              className="inline-flex items-center gap-2 px-4 py-2 bg-nara-orange text-nara-charcoal font-medium rounded-lg hover:bg-nara-orange/90 transition-colors disabled:opacity-50"
            >
              {isSavingTemplate && <Loader2 className="h-4 w-4 animate-spin" />}
              {isSavingTemplate ? t("naraShape.createDialog.creating") : t("naraShape.createDialog.create")}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label
              htmlFor="templateName"
              className="block text-sm font-medium text-foreground mb-1.5"
            >
              {t("naraShape.createDialog.nameLabel")} <span className="text-red-500">*</span>
            </label>
            <input
              id="templateName"
              type="text"
              value={templateLabel}
              onChange={(e) => setTemplateLabel(e.target.value)}
              placeholder={t("naraShape.createDialog.namePlaceholder")}
              maxLength={255}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-nara-orange/50"
              onKeyDown={(e) => {
                if (e.key === "Enter" && templateLabel.trim()) handleSaveTemplate();
              }}
              autoFocus
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t("naraShape.createDialog.nameHint")}
            </p>
          </div>
          <div>
            <label htmlFor="templateMode" className="block text-sm font-medium text-foreground mb-1.5">
              {t("naraShape.detail.inputMode")}
            </label>
            <select
              id="templateMode"
              value={newMode}
              onChange={(e) => setNewMode(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
            >
              {["transcription", "project", "extraction"].map((mode) => (
                <option key={mode} value={mode}>
                  {t(`naraShape.templateInputMode.${mode}`)}
                </option>
              ))}
            </select>
            {newMode === "extraction" && <p className="text-xs text-muted-foreground mt-1">{t("naraShape.detail.extractionHint")}</p>}
          </div>
          {createError && (
            <p className="text-sm text-red-500">{createError}</p>
          )}
        </div>
      </KiiDialogLayout>
    </div>
  );
}
